import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useParams, useSearchParams } from "react-router-dom";
import { Modal } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import moment from "moment";
import API from "utils/helpers/APIHelper";
import { ENDPOINTS } from "API/apiConstants";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { currencySymbol } from "shared/CurrencySymbol";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "redux/store";
import BtnLikeIcon from "components/BtnLikeIcon/BtnLikeIcon";
import { getallupdateProperties } from "redux/reducers/Properties/PropertiesSlice";
import StartRating from "components/StartRating/StartRating";
import DetailPagetLayout from "containers/ListingDetailPage/Layout";
import RenderSectionDetails from "containers/ListingDetailPage/listing-stay-detail/ListingStayDetailsPage/RenderSectionDetails";
import PropertyDescription from "containers/ListingDetailPage/listing-stay-detail/ListingStayDetailsPage/PropertyDescription";
import AmenitiesFeatures from "containers/ListingDetailPage/listing-stay-detail/ListingStayDetailsPage/AmenitiesFeatures";
import Policy from "containers/ListingDetailPage/listing-stay-detail/ListingStayDetailsPage/Policy";
import FinancialDetails from "containers/ListingDetailPage/listing-stay-detail/ListingStayDetailsPage/FinancialDetails";
import Cancellation from "containers/ListingDetailPage/listing-stay-detail/ListingStayDetailsPage/Cancellation";
import Universities from "containers/ListingDetailPage/listing-stay-detail/ListingStayDetailsPage/Universities";
import Recommendations from "containers/ListingDetailPage/listing-stay-detail/ListingStayDetailsPage/Recommendations";
import ListingMap from "containers/ListingDetailPage/listing-stay-detail/ListingStayDetailsPage/ListingMap";
import Roomies from "containers/ListingDetailPage/listing-stay-detail/ListingStayDetailsPage/Roomies";
import Review from "containers/ListingDetailPage/listing-stay-detail/ListingStayDetailsPage/Review";
import HostInformation from "containers/ListingDetailPage/listing-stay-detail/ListingStayDetailsPage/HostInformation";
import SimilarProperty from "containers/ListingDetailPage/listing-stay-detail/ListingStayDetailsPage/SimilarProperty";
import RoomBookingCard from "containers/RoomBookingPage/RoomBookingCard";
import EntirePropertyBookingCard from "containers/HousePage/EntirePropertyBookingCard";
import { track, roomItem, CURRENCY } from "utils/analytics/events";
import ShareButton from "components/ShareButton/ShareButton";
import SEO from "components/SEO/SEO";
import JsonLd from "components/JsonLd/JsonLd";
import PropertyImageGallery from "containers/ListingDetailPage/listing-stay-detail/ListingStayDetailsPage/Propertyimagegallery";
import ImageModal from "containers/ListingDetailPage/listing-stay-detail/ImageModal";

interface RoomData {
  id: number;
  name: string;
  public_token: string;
  price_per_month: string;
  max_guests?: number | null;
  booked_dates: [string, string][];
  availability_start?: string | null;
  availability_end?: string | null;
  size?: number | null;
  room_type?: { id: number; name: string } | null;
  bed_type?: { id: number; name: string } | null;
  bathroom_type?: string | null;
  room_images: { id: number; url: string }[];
  to_book: boolean;
  next_available_date?: string | null;
}

interface PropertyData {
  id: number;
  slug: string;
  title: string;
  city?: string | null;
  neighborhood?: string | null;
  cover_image?: { id: number; url: string } | null;
  images?: { id?: number; url: string }[] | null;
  is_shared_property: boolean;
  instant_booking: boolean;
  allow_extra_guest?: boolean;
  per_extra_guest_amount?: number | string | null;
  service_fee: number;
  availability_start?: string | null;
  availability_end?: string | null;
  minimum_months_of_booking?: number | null;
  total_bedrooms?: number | null;
  total_bathrooms?: string | number | null;
}

interface PropertyRoomsResponse {
  property: PropertyData;
  rooms: RoomData[];
}

const DATE_FORMAT = "YYYY-MM-DD";
const MIN_STAY_DAYS = 30;
const INITIAL_ROOMS = 6;

type Tone = "good" | "soon" | "none";
type SortKey = "price" | "soonest";
type BathFilter = "all" | "private" | "shared";

const TEXT_CLASSES: Record<Tone, string> = {
  good: "text-green-600 dark:text-green-400",
  soon: "text-amber-600 dark:text-amber-400",
  none: "text-neutral-500",
};

const BATH_LABELS: Record<string, string> = {
  ensuite_bathroom: "Ensuite bathroom",
  external_private_bathroom: "Private bathroom",
  shared_bathroom: "Shared bathroom",
};

const isPrivateBath = (value?: string | null) =>
  value === "ensuite_bathroom" || value === "external_private_bathroom";

const parseDate = (value?: string | null) =>
  moment(value, [DATE_FORMAT, "MMM DD,YYYY"], true);

// With dates chosen, the server's `to_book` says if the room is free for them.
// Otherwise show when the room next frees up.
const roomStatus = (
  room: RoomData,
  hasDates: boolean
): { text: string; tone: Tone; free: boolean } => {
  if (hasDates) {
    return room.to_book
      ? { text: "Free for your dates", tone: "good", free: true }
      : { text: "Not free for your dates", tone: "none", free: false };
  }
  const value = room.next_available_date;
  if (value === "unavailable") {
    return { text: "Not available", tone: "none", free: false };
  }
  if (!value) return { text: "Available now", tone: "good", free: true };
  const parsed = parseDate(value);
  if (!parsed.isValid() || parsed.isSameOrBefore(moment(), "day")) {
    return { text: "Available now", tone: "good", free: true };
  }
  return {
    text: `Free from ${parsed.format("MMM D, YYYY")}`,
    tone: "soon",
    free: true,
  };
};

const soonestKey = (room: RoomData): number => {
  const value = room.next_available_date;
  if (value === "unavailable") return Number.MAX_SAFE_INTEGER;
  if (!value) return 0;
  const parsed = parseDate(value);
  return parsed.isValid() ? parsed.valueOf() : 0;
};

const SECTION_TABS = [
  { id: "rooms", label: "Rooms" },
  { id: "about", label: "About" },
  { id: "amenities", label: "Amenities" },
  { id: "rules", label: "Rules" },
  { id: "location", label: "Location" },
  { id: "reviews", label: "Reviews" },
  { id: "host", label: "Host" },
];

const chipClass = (active: boolean) =>
  `px-3.5 py-1.5 rounded-full text-sm border transition ${
    active
      ? "bg-neutral-900 text-white border-neutral-900 dark:bg-white dark:text-neutral-900 dark:border-white"
      : "border-neutral-300 dark:border-neutral-600 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800"
  }`;

const inputClass =
  "w-full rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 px-3 py-2 text-base sm:text-sm text-neutral-900 dark:text-white focus:outline-none focus:border-orange-400";

const RoomPhotoViewer: FC<{
  images: string[];
  alt: string;
  onInteract?: () => void;
}> = ({ images, alt, onInteract }) => {
  const [index, setIndex] = useState(0);
  const [touchX, setTouchX] = useState<number | null>(null);
  const [interacted, setInteracted] = useState(false);
  const count = images.length;
  const markInteracted = () => {
    if (!interacted) {
      setInteracted(true);
      onInteract?.();
    }
  };
  const go = (delta: number) => {
    markInteracted();
    setIndex((i) => (i + delta + count) % count);
  };

  if (count === 0) return null;

  return (
    <div>
      <div
        className="relative aspect-[4/3] rounded-xl overflow-hidden bg-neutral-100 dark:bg-neutral-800 select-none"
        onTouchStart={(e) => setTouchX(e.touches[0].clientX)}
        onTouchEnd={(e) => {
          if (touchX === null || count < 2) return;
          const diff = e.changedTouches[0].clientX - touchX;
          if (Math.abs(diff) > 40) go(diff < 0 ? 1 : -1);
          setTouchX(null);
        }}
      >
        <img
          src={images[index]}
          alt={`${alt} photo ${index + 1}`}
          className="absolute inset-0 w-full h-full object-cover"
        />
        {count > 1 && (
          <>
            <button
              type="button"
              aria-label="Previous photo"
              onClick={() => go(-1)}
              className="hidden sm:flex absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 items-center justify-center rounded-full bg-white/90 text-neutral-900 shadow"
            >
              <i className="las la-angle-left"></i>
            </button>
            <button
              type="button"
              aria-label="Next photo"
              onClick={() => go(1)}
              className="hidden sm:flex absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 items-center justify-center rounded-full bg-white/90 text-neutral-900 shadow"
            >
              <i className="las la-angle-right"></i>
            </button>
            <span className="absolute bottom-2 right-2 text-xs rounded-md bg-black/60 text-white px-2 py-0.5">
              {index + 1} / {count}
            </span>
          </>
        )}
      </div>
      {count > 1 && (
        <div className="hidden sm:flex gap-1.5 mt-2 overflow-x-auto pb-1">
          {images.map((src, i) => (
            <button
              key={`${src}-${i}`}
              type="button"
              aria-label={`Show photo ${i + 1}`}
              onClick={() => {
                markInteracted();
                setIndex(i);
              }}
              className={`flex-none w-14 h-11 rounded-md overflow-hidden border-2 ${
                i === index
                  ? "border-neutral-900 dark:border-white"
                  : "border-transparent opacity-70 hover:opacity-100"
              }`}
            >
              <img src={src} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
      {count > 1 && (
        <div className="flex sm:hidden justify-center gap-1.5 mt-2">
          {images.slice(0, 10).map((_, i) => (
            <span
              key={i}
              className={`w-1.5 h-1.5 rounded-full ${
                i === index
                  ? "bg-neutral-900 dark:bg-white"
                  : "bg-neutral-300 dark:bg-neutral-600"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const HouseContent: FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const currentUser = useSelector((state: RootState) => state.user.currentUser);
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const [data, setData] = useState<PropertyRoomsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [notAvailable, setNotAvailable] = useState(false);

  const [checkIn, setCheckIn] = useState<string | null>(searchParams.get("in"));
  const [checkOut, setCheckOut] = useState<string | null>(
    searchParams.get("out")
  );
  const [bath, setBath] = useState<BathFilter>("all");
  const [sort, setSort] = useState<SortKey>("price");
  const [expanded, setExpanded] = useState(false);
  const [propertyData, setPropertyData] = useState<any>(null);
  const [imageModal, setImageModal] = useState(false);
  const [isOpenModalAmenities, setIsOpenModalAmenities] = useState(false);
  const [activeTab, setActiveTab] = useState("rooms");
  const [drawerToken, setDrawerToken] = useState<string | null>(null);
  const isMobile = useMediaQuery("(max-width: 1023px)");
  const closeImageModal = useCallback(() => setImageModal(false), []);

  // Dates only count once both are set and the stay is at least 30 days.
  const stayDays =
    checkIn && checkOut ? moment(checkOut).diff(moment(checkIn), "days") : null;
  const appliedIn = stayDays !== null && stayDays >= MIN_STAY_DAYS ? checkIn : null;
  const appliedOut = appliedIn ? checkOut : null;
  const hasDates = Boolean(appliedIn && appliedOut);
  const datesTooShort = stayDays !== null && stayDays < MIN_STAY_DAYS;
  const datesQuery = hasDates ? `?in=${appliedIn}&out=${appliedOut}` : "";

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // Full property (photos, description, amenities, rules, map...) for the reused sections.
  useEffect(() => {
    let cancelled = false;
    const loadProperty = async () => {
      try {
        const response: any = await dispatch(
          getallupdateProperties({ propertyId: slug || "", queryParams: undefined } as any)
        );
        const property = response?.payload?.property;
        if (!cancelled && property) setPropertyData(property);
      } catch (error) {
        console.error("Error fetching property data:", error);
      }
    };
    loadProperty();
    return () => {
      cancelled = true;
    };
  }, [slug, dispatch]);

  // Highlight the tab of the section being read.
  useEffect(() => {
    if (!propertyData) return;
    const nodes = SECTION_TABS.map((t) => document.getElementById(t.id)).filter(
      Boolean
    ) as HTMLElement[];
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveTab(entry.target.id);
        });
      },
      { rootMargin: "-20% 0px -70% 0px" }
    );
    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [propertyData]);

  // Load the house. Reload (keeping the old rooms on screen) when valid dates change.
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setNotAvailable(false);
      setRefreshing(true);
      try {
        const query = new URLSearchParams();
        if (appliedIn && appliedOut) {
          query.set("in", appliedIn);
          query.set("out", appliedOut);
        }
        const response = await API.get(
          ENDPOINTS.GET_PROPERTY_ROOMS(slug || "", query.toString())
        );
        if (cancelled) return;
        const raw: any = response;
        const body: PropertyRoomsResponse | undefined = raw?.rooms
          ? raw
          : raw?.data;
        if (!body?.rooms) throw new Error("Unexpected house response");
        setData(body);
      } catch (error) {
        if (!cancelled) setNotAvailable(true);
      } finally {
        if (!cancelled) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [slug, appliedIn, appliedOut]);

  // Keep the dates in the address bar so the link can be shared.
  // Only the two date parameters are touched: tracking tags (utm_*) and anything
  // else in the address stay exactly as they arrived.
  useEffect(() => {
    const current = window.location.search.replace(/^\?/, "");
    const next = new URLSearchParams(current);
    if (appliedIn && appliedOut) {
      next.set("in", appliedIn);
      next.set("out", appliedOut);
    } else {
      next.delete("in");
      next.delete("out");
    }
    if (next.toString() !== current) {
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedIn, appliedOut]);

  const rooms = useMemo(() => data?.rooms || [], [data]);

  const hasPrivate = rooms.some((r) => isPrivateBath(r.bathroom_type));
  const hasShared = rooms.some((r) => r.bathroom_type === "shared_bathroom");
  const showBathFilter = hasPrivate && hasShared;

  const entireMode = data ? !data.property.is_shared_property : false;

  const list = useMemo(() => {
    const filtered = rooms.filter((r) => {
      if (bath === "private") return isPrivateBath(r.bathroom_type);
      if (bath === "shared") return r.bathroom_type === "shared_bathroom";
      return true;
    });
    const sorted = [...filtered].sort((a, b) =>
      sort === "soonest"
        ? soonestKey(a) - soonestKey(b)
        : (Number(a.price_per_month) || 0) - (Number(b.price_per_month) || 0)
    );
    // With dates chosen, rooms that are taken go to the bottom (stable sort).
    return hasDates && !entireMode
      ? sorted.sort((a, b) => Number(b.to_book) - Number(a.to_book))
      : sorted;
  }, [rooms, bath, sort, hasDates, entireMode]);

  // ---- Analytics ----
  // The room list was shown (once per house, not on every filter change).
  const listTrackedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!data || listTrackedFor.current === data.property.slug) return;
    listTrackedFor.current = data.property.slug;
    if (!data.property.is_shared_property) {
      track("view_item", {
        property_slug: data.property.slug,
        item_name: data.property.title,
        booking_type: "entire_property",
        has_dates: hasDates,
      });
      return;
    }
    track("view_item_list", {
      item_list_id: "house_rooms",
      item_list_name: data.property.title,
      property_slug: data.property.slug,
      rooms_count: data.rooms.length,
      has_dates: hasDates,
      items: data.rooms.slice(0, 10).map((r) => roomItem(r, data.property)),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Valid dates were chosen.
  const datesTrackedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!appliedIn || !appliedOut) return;
    const key = `${appliedIn}|${appliedOut}`;
    if (datesTrackedFor.current === key) return;
    datesTrackedFor.current = key;
    track("select_dates", {
      source: "house_page",
      property_slug: slug,
      nights: moment(appliedOut).diff(moment(appliedIn), "days"),
      lead_days: moment(appliedIn).diff(moment().startOf("day"), "days"),
    });
  }, [appliedIn, appliedOut, slug]);

  const openRoom = (room: RoomData, source: "tile" | "side_card") => {
    if (data) {
      const item = roomItem(room, data.property);
      track("select_item", {
        item_list_id: "house_rooms",
        item_list_name: data.property.title,
        source,
        has_dates: hasDates,
        items: [item],
      });
      track("view_item", {
        currency: CURRENCY,
        value: item.price,
        source,
        has_dates: hasDates,
        items: [item],
      });
    }
    setDrawerToken(room.public_token);
  };

  if (loading) {
    return (
      <div className="container max-w-6xl mx-auto py-16 px-4 text-neutral-500">
        Loading house...
      </div>
    );
  }

  // The new page only opens active, approved properties by slug. Anything else
  // (a host previewing a draft, a link with an id, a removed listing) goes to the
  // current listing page, which handles those cases exactly as before.
  if (notAvailable && !data) {
    return (
      <Navigate
        to={`/legacy/listing-stay-detail/${slug || ""}${window.location.search}`}
        replace
      />
    );
  }

  if (!data) return null;

  const { property } = data;

  // Entire property: one booking for the whole place. Shared house: pick a room.
  const isEntire = !property.is_shared_property;
  const tabs = isEntire
    ? SECTION_TABS.map((tab) =>
        tab.id === "rooms" ? { ...tab, label: "The place" } : tab
      )
    : SECTION_TABS;

  const money = (amount: number) =>
    `${currencySymbol()} ${
      Number.isInteger(amount) ? amount : amount.toFixed(2)
    }`;
  const entirePrice = Number(propertyData?.price_per_month) || 0;
  const prices = isEntire
    ? entirePrice > 0
      ? [entirePrice]
      : []
    : rooms.map((r) => Number(r.price_per_month) || 0);
  const lowestPrice = prices.length ? Math.min(...prices) : 0;
  const freeCount = rooms.filter((r) => roomStatus(r, hasDates).free).length;

  const cheapestFree = [...rooms]
    .filter((r) => roomStatus(r, hasDates).free)
    .sort(
      (a, b) =>
        (Number(a.price_per_month) || 0) - (Number(b.price_per_month) || 0)
    )
    .slice(0, 3);
  const drawerRoom = rooms.find((r) => r.public_token === drawerToken) || null;
  const drawerStatus = drawerRoom ? roomStatus(drawerRoom, hasDates) : null;

  const location = [property.neighborhood, property.city]
    .filter(Boolean)
    .join(", ");

  const fallbackImage = property.cover_image?.url;
  const sortAddress = [
    propertyData?.neighborhood || property.neighborhood,
    propertyData?.city || property.city,
    propertyData?.state,
  ]
    .filter(Boolean)
    .join(", ");
  const userLocation = {
    lat: Number(propertyData?.latitude) || 0,
    lng: Number(propertyData?.longitude) || 0,
  };

  const showAll = expanded;
  const visible = showAll ? list : list.slice(0, INITIAL_ROOMS);

  const today = moment().format(DATE_FORMAT);
  const minCheckOut = checkIn
    ? moment(checkIn).add(MIN_STAY_DAYS, "days").format(DATE_FORMAT)
    : today;

  // Two cards exist in the page (mobile and desktop); scroll to the one that is showing.
  const scrollToBookingCard = () => {
    window.setTimeout(() => {
      const cards = Array.from(
        document.querySelectorAll<HTMLElement>("#booking-card")
      );
      const visibleCard = cards.find((card) => card.offsetParent !== null);
      visibleCard?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 250);
  };

  const scrollToRooms = () => {
    document
      .getElementById("rooms")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const datesPanel = (
    <>
      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className="text-xs text-neutral-500">Check in</span>
          <input
            type="date"
            min={today}
            value={checkIn || ""}
            onChange={(e) => {
              const value = e.target.value || null;
              setCheckIn(value);
              if (
                value &&
                checkOut &&
                moment(checkOut).diff(moment(value), "days") < MIN_STAY_DAYS
              ) {
                setCheckOut(null);
              }
            }}
            className={inputClass}
          />
        </label>
        <label className="block">
          <span className="text-xs text-neutral-500">Check out</span>
          <input
            type="date"
            min={minCheckOut}
            value={checkOut || ""}
            onChange={(e) => setCheckOut(e.target.value || null)}
            className={inputClass}
          />
        </label>
      </div>
      <p className="text-xs text-neutral-400 mt-1.5 min-h-[1rem]">
        {datesTooShort ? (
          "Stays are at least 30 days."
        ) : hasDates ? (
          <>
            Showing rooms for these dates.{" "}
            <button
              type="button"
              onClick={() => {
                setCheckIn(null);
                setCheckOut(null);
              }}
              className="underline hover:text-orange-500"
            >
              Clear dates
            </button>
          </>
        ) : (
          "Add your dates to see which rooms are free."
        )}
      </p>
    </>
  );

  // SEO: one clean address per house, whatever dates or tracking tags are in the URL.
  const seoPath = `/h/${property.slug}`;
  const highestPrice = prices.length ? Math.max(...prices) : 0;
  const seoDescription = [
    `${property.title}${location ? ` in ${location}` : ""}:`,
    isEntire
      ? "Entire property for rent"
      : rooms.length > 0
      ? `${rooms.length} furnished ${rooms.length === 1 ? "room" : "rooms"}`
      : "Student rooms",
    lowestPrice > 0
      ? `from ${currencySymbol()}${lowestPrice} per month.`
      : "available to book.",
    "Compare rooms, check availability and send a booking request online.",
  ].join(" ");
  const houseSchema: Record<string, any> = {
    "@context": "https://schema.org",
    "@type": "LodgingBusiness",
    name: property.title,
    url: `https://www.nomadroof.com${seoPath}`,
    description: seoDescription,
    ...(fallbackImage ? { image: fallbackImage } : {}),
    address: {
      "@type": "PostalAddress",
      ...(property.neighborhood
        ? { addressNeighborhood: property.neighborhood }
        : {}),
      ...(property.city ? { addressLocality: property.city } : {}),
    },
    ...(lowestPrice > 0
      ? {
          priceRange:
            highestPrice > lowestPrice
              ? `${currencySymbol()}${lowestPrice} - ${currencySymbol()}${highestPrice} per month`
              : `${currencySymbol()}${lowestPrice} per month`,
        }
      : {}),
  };
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://www.nomadroof.com/" },
      { "@type": "ListItem", position: 2, name: property.title, item: `https://www.nomadroof.com${seoPath}` },
    ],
  };

  return (
    <div className="nc-HousePage container max-w-6xl mx-auto pt-6 pb-10 px-4">
      <SEO
        title={`${property.title}${location ? ` · ${location}` : ""}`}
        description={seoDescription}
        canonical={seoPath}
        ogImage={fallbackImage}
      />
      <JsonLd schema={[houseSchema, breadcrumbSchema]} />
      {/* HEADER */}
      <div className="flex items-start justify-between gap-3 py-4">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-semibold">
          {property.title}
        </h1>
        <div className="flex-none flex items-center gap-2">
          <ShareButton
            propertyId={property.id}
            slug={property.slug}
            title={property.title}
            city={property.city || undefined}
            variant="full"
            surface="property_detail"
            path={`/h/${property.slug}${datesQuery}`}
            className="!rounded-full border border-neutral-300 dark:border-neutral-600"
          />
          {/* Save: same rule as the current listing page (students only) */}
          {currentUser?.is_student && propertyData && (
            <BtnLikeIcon
              isLiked={propertyData.is_favorite}
              propertyId={propertyData.id}
              path="propertyDetail"
              propertyData={propertyData}
              setPropertyData={setPropertyData}
              colorClass="border border-neutral-300 dark:border-neutral-600 text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800"
              className="!w-10 !h-10"
            />
          )}
        </div>
      </div>
      <div className="flex items-center flex-wrap gap-3 mb-5">
        {propertyData && <StartRating propertyData={propertyData} />}
        {sortAddress && (
          <div className="flex items-center text-sm text-neutral-600 dark:text-neutral-400">
            <i className="las la-map-marker-alt text-lg"></i>
            <span className="ml-1.5">{sortAddress}</span>
          </div>
        )}
        <span className="text-sm text-neutral-500">
          {isEntire
            ? "Entire property"
            : `${rooms.length} ${rooms.length === 1 ? "room" : "rooms"}`}
        </span>
      </div>

      {/* HOUSE PHOTOS: same gallery as the current listing page */}
      {propertyData ? (
        <PropertyImageGallery
          coverImage={propertyData.cover_image}
          placeImages={propertyData.place_images || []}
          propertyTitle={propertyData.title}
          onOpenGallery={() => setImageModal(true)}
        />
      ) : (
        <div className="h-56 sm:h-72 md:h-[22rem] rounded-2xl bg-neutral-100 dark:bg-neutral-800 overflow-hidden">
          {fallbackImage && (
            <img src={fallbackImage} alt="" className="w-full h-full object-cover" />
          )}
        </div>
      )}

      {/* SECTION TABS */}
      <nav
        aria-label="Page sections"
        className="sticky top-0 z-30 -mx-4 px-4 mt-6 bg-white/95 dark:bg-neutral-900/95 backdrop-blur border-b border-neutral-200 dark:border-neutral-700"
      >
        <div className="flex gap-6 overflow-x-auto whitespace-nowrap">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => {
                setActiveTab(tab.id);
                document
                  .getElementById(tab.id)
                  ?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              className={`py-3 text-sm border-b-2 transition ${
                activeTab === tab.id
                  ? "border-neutral-900 dark:border-white text-neutral-900 dark:text-white font-medium"
                  : "border-transparent text-neutral-500 hover:text-neutral-900 dark:hover:text-white"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 mt-8">
        {/* LEFT COLUMN */}
        <div className="lg:col-span-2 min-w-0">
          {/* THE WHOLE PLACE (entire property) */}
          {isEntire ? (
            <section id="rooms" className="scroll-mt-20">
              <h2 className="text-xl font-semibold mb-3">The whole place</h2>
              <div className="flex flex-wrap gap-2 text-sm mb-4">
                {(propertyData?.total_bedrooms ?? property.total_bedrooms) ? (
                  <span className="rounded-lg bg-neutral-100 dark:bg-neutral-800 px-3 py-1.5">
                    {propertyData?.total_bedrooms ?? property.total_bedrooms} bedrooms
                  </span>
                ) : null}
                {(propertyData?.total_bathrooms ?? property.total_bathrooms) ? (
                  <span className="rounded-lg bg-neutral-100 dark:bg-neutral-800 px-3 py-1.5">
                    {propertyData?.total_bathrooms ?? property.total_bathrooms} bathrooms
                  </span>
                ) : null}
                {propertyData?.guest_number ? (
                  <span className="rounded-lg bg-neutral-100 dark:bg-neutral-800 px-3 py-1.5">
                    Up to {propertyData.guest_number} guests
                  </span>
                ) : null}
              </div>

              {/* Booking card: here on mobile, in the right column on desktop */}
              <div className="lg:hidden">
                {propertyData ? (
                  <EntirePropertyBookingCard
                    property={propertyData}
                    checkIn={checkIn}
                    checkOut={checkOut}
                    onDatesChange={(nextIn, nextOut) => {
                      setCheckIn(nextIn);
                      setCheckOut(nextOut);
                    }}
                  />
                ) : (
                  <p className="text-sm text-neutral-500">Loading booking details...</p>
                )}
              </div>
            </section>
          ) : null}

          <section
            id={isEntire ? "rooms-list" : "rooms"}
            className={`scroll-mt-20 ${isEntire ? "mt-8" : ""}`}
          >
            <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
              <h2 className="text-xl font-semibold">
                {isEntire ? "Rooms in this place" : "Choose your room"}
              </h2>
              <span className="text-sm text-neutral-500">
                {hasDates && !isEntire
                  ? `${freeCount} of ${rooms.length} free for your dates`
                  : `${rooms.length} ${rooms.length === 1 ? "room" : "rooms"}`}
              </span>
            </div>
            {!isEntire && (
            <p className="lg:hidden text-sm text-neutral-500 -mt-1 mb-3">
              Rooms from{" "}
              <span className="font-semibold text-neutral-900 dark:text-white">
                {money(lowestPrice)}
              </span>{" "}
              / month
            </p>
            )}

            {/* Dates (mobile only: on desktop they live in the side card) */}
            {!isEntire && <div className="lg:hidden mb-4">{datesPanel}</div>}

            <div className="flex flex-wrap items-center gap-2 mb-4">
              {showBathFilter && (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      setBath("all");
                      track("filter_rooms", { filter_type: "bathroom", filter_value: "all" });
                    }}
                    className={chipClass(bath === "all")}
                  >
                    All
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setBath("private");
                      track("filter_rooms", { filter_type: "bathroom", filter_value: "private" });
                    }}
                    className={chipClass(bath === "private")}
                  >
                    Private bathroom
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setBath("shared");
                      track("filter_rooms", { filter_type: "bathroom", filter_value: "shared" });
                    }}
                    className={chipClass(bath === "shared")}
                  >
                    Shared bathroom
                  </button>
                </>
              )}
              {!isEntire && (
              <select
                value={sort}
                onChange={(e) => {
                  setSort(e.target.value as SortKey);
                  track("filter_rooms", { filter_type: "sort", filter_value: e.target.value });
                }}
                aria-label="Sort rooms"
                className="ml-auto rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 px-3 py-1.5 text-base sm:text-sm text-neutral-700 dark:text-neutral-200"
              >
                <option value="price">Lowest price</option>
                <option value="soonest">Available soonest</option>
              </select>
              )}
            </div>

            {list.length === 0 ? (
              <p className="text-sm text-neutral-500 py-6">
                No rooms match this filter.
              </p>
            ) : (
              <div
                className={`grid grid-cols-1 sm:grid-cols-2 gap-2.5 ${
                  refreshing ? "opacity-60" : ""
                }`}
              >
                {visible.map((room) => {
                  const status = isEntire
                    ? { text: "", tone: "none" as Tone, free: true }
                    : roomStatus(room, hasDates);
                  const thumb = room.room_images[0]?.url || fallbackImage;
                  return (
                    <button
                      key={room.id}
                      type="button"
                      onClick={() => openRoom(room, "tile")}
                      className={`flex items-center gap-3 text-left w-full rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 p-2.5 transition hover:border-neutral-400 dark:hover:border-neutral-500 ${
                        !status.free ? "opacity-60" : ""
                      }`}
                    >
                      <div className="w-16 h-16 rounded-lg overflow-hidden bg-neutral-100 dark:bg-neutral-800 flex-none">
                        {thumb && (
                          <img
                            src={thumb}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">
                          {room.name}
                          {!isEntire &&
                            ` · ${money(Number(room.price_per_month) || 0)}`}
                        </p>
                        {!isEntire && (
                          <p
                            className={`text-xs truncate ${
                              TEXT_CLASSES[status.tone]
                            }`}
                          >
                            {status.text}
                          </p>
                        )}
                        {room.bathroom_type && (
                          <p className="text-[11px] text-neutral-400 truncate">
                            {BATH_LABELS[room.bathroom_type] || ""}
                          </p>
                        )}
                      </div>
                      <i className="las la-angle-right text-neutral-400 flex-none"></i>
                    </button>
                  );
                })}
              </div>
            )}

            {list.length > INITIAL_ROOMS && (
              <button
                type="button"
                onClick={() => setExpanded((prev) => !prev)}
                className="w-full mt-3 rounded-xl border border-neutral-300 dark:border-neutral-600 py-2.5 text-sm hover:bg-neutral-50 dark:hover:bg-neutral-800"
              >
                {showAll ? "Show fewer rooms" : `Show all ${list.length} rooms`}
              </button>
            )}
          </section>

          {/* OTHER SECTIONS: the current listing page components, grouped under the tabs */}
          {propertyData && (
            <div className="mt-10 space-y-8 lg:space-y-10">
              <div id="about" className="scroll-mt-20 space-y-8 lg:space-y-10">
                <RenderSectionDetails
                  propertyData={propertyData}
                  slug={slug || ""}
                  setPropertyData={setPropertyData}
                />
                <PropertyDescription propertyData={propertyData} />
                <Roomies propertyData={propertyData} />
              </div>

              <div id="amenities" className="scroll-mt-20">
                <AmenitiesFeatures
                  propertyData={propertyData}
                  openModalAmenities={() => setIsOpenModalAmenities(true)}
                  isOpenModalAmenities={isOpenModalAmenities}
                  closeModalAmenities={() => setIsOpenModalAmenities(false)}
                />
              </div>

              <div id="rules" className="scroll-mt-20 space-y-8 lg:space-y-10">
                <Policy propertyData={propertyData} />
                <FinancialDetails propertyData={propertyData} />
                <Cancellation propertyData={propertyData} />
              </div>

              <div id="location" className="scroll-mt-20 space-y-8 lg:space-y-10">
                <Universities propertyData={propertyData} />
                <Recommendations propertyData={propertyData} />
                <ListingMap propertyData={propertyData} userLocation={userLocation} />
              </div>

              <div id="reviews" className="scroll-mt-20">
                <Review propertyData={propertyData} />
              </div>

              <div id="host" className="scroll-mt-20 space-y-8 lg:space-y-10">
                <HostInformation propertyData={propertyData} />
                <SimilarProperty propertyData={propertyData} />
              </div>
            </div>
          )}
        </div>

        {/* SIDE CARD (desktop) */}
        <div className="hidden lg:block lg:col-span-1">
          {isEntire ? (
            propertyData ? (
              <EntirePropertyBookingCard
                property={propertyData}
                checkIn={checkIn}
                checkOut={checkOut}
                onDatesChange={(nextIn, nextOut) => {
                  setCheckIn(nextIn);
                  setCheckOut(nextOut);
                }}
              />
            ) : (
              <p className="text-sm text-neutral-500">Loading booking details...</p>
            )
          ) : (
          <div className="sticky top-16 rounded-2xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 p-5">
            <p className="text-xs text-neutral-500">Rooms from</p>
            <p className="mb-4">
              <span className="text-2xl font-semibold">{money(lowestPrice)}</span>
              <span className="text-sm text-neutral-500"> / month</span>
            </p>
            {datesPanel}
            {hasDates && (
              <p className="text-sm text-green-600 dark:text-green-400 mt-2">
                {freeCount} of {rooms.length} rooms free for your dates
              </p>
            )}
            {cheapestFree.length > 0 && (
              <div className="mt-4">
                <p className="text-xs text-neutral-500 mb-1">
                  {hasDates ? "Cheapest free rooms" : "Lowest price rooms"}
                </p>
                {cheapestFree.map((room) => (
                  <button
                    key={room.id}
                    type="button"
                    onClick={() => openRoom(room, "side_card")}
                    className="w-full flex items-center justify-between py-2 text-sm border-b border-neutral-100 dark:border-neutral-800 hover:text-orange-500"
                  >
                    <span>{room.name}</span>
                    <span>
                      {money(Number(room.price_per_month) || 0)} &rarr;
                    </span>
                  </button>
                ))}
              </div>
            )}
            <ButtonPrimary className="w-full mt-4" onClick={scrollToRooms}>
              See all {rooms.length} rooms
            </ButtonPrimary>
            <p className="text-xs text-neutral-400 mt-2 text-center">
              Tap a room to see details and book.
            </p>
          </div>
          )}
        </div>
      </div>

      <Modal
        opened={Boolean(drawerRoom)}
        onClose={() => setDrawerToken(null)}
        withCloseButton={false}
        centered={!isMobile}
        size={isMobile ? "100%" : 720}
        padding={0}
        radius={isMobile ? 0 : 16}
        zIndex={1000}
        lockScroll
        overlayProps={{ opacity: 0.6, blur: 2 }}
        transitionProps={{ transition: isMobile ? "slide-up" : "fade", duration: 200 }}
        classNames={{ content: "!bg-white dark:!bg-neutral-900 !text-neutral-900 dark:!text-neutral-100" }}
        styles={
          isMobile
            ? {
                inner: { alignItems: "flex-end", padding: 0 },
                content: {
                  borderRadius: "16px 16px 0 0",
                  maxHeight: "92dvh",
                  width: "100%",
                },
              }
            : undefined
        }
      >
        {drawerRoom && (
          <div className="p-4 sm:p-5">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="min-w-0">
                <h2 className="text-lg sm:text-xl font-semibold truncate">
                  {drawerRoom.name}
                </h2>
                <p className="text-xs text-neutral-500 truncate">
                  {property.title}
                </p>
              </div>
              <button
                type="button"
                aria-label="Close"
                onClick={() => setDrawerToken(null)}
                className="flex-none text-2xl leading-none px-2 text-neutral-500 hover:text-neutral-900 dark:hover:text-white"
              >
                &times;
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div className="min-w-0">
                <RoomPhotoViewer
                  key={drawerRoom.id}
                  images={drawerRoom.room_images.map((image) => image.url)}
                  alt={drawerRoom.name}
                  onInteract={() =>
                    track("view_room_photos", {
                      property_slug: property.slug,
                      item_id: String(drawerRoom.id),
                      photos_count: drawerRoom.room_images.length,
                    })
                  }
                />

                <div className="flex flex-wrap gap-2 mt-3 text-xs">
                  {drawerRoom.bathroom_type && (
                    <span className="rounded-lg bg-neutral-100 dark:bg-neutral-800 px-2.5 py-1">
                      {BATH_LABELS[drawerRoom.bathroom_type] || ""}
                    </span>
                  )}
                  {drawerRoom.bed_type?.name && (
                    <span className="rounded-lg bg-neutral-100 dark:bg-neutral-800 px-2.5 py-1">
                      {drawerRoom.bed_type.name}
                    </span>
                  )}
                  {drawerRoom.max_guests ? (
                    <span className="rounded-lg bg-neutral-100 dark:bg-neutral-800 px-2.5 py-1">
                      {drawerRoom.max_guests}{" "}
                      {drawerRoom.max_guests === 1 ? "guest" : "guests"}
                    </span>
                  ) : null}
                  {drawerRoom.size ? (
                    <span className="rounded-lg bg-neutral-100 dark:bg-neutral-800 px-2.5 py-1">
                      {drawerRoom.size} sq ft
                    </span>
                  ) : null}
                  {drawerStatus && !isEntire && (
                    <span
                      className={`rounded-lg px-2.5 py-1 ${
                        drawerStatus.tone === "good"
                          ? "bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300"
                          : drawerStatus.tone === "soon"
                          ? "bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200"
                          : "bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300"
                      }`}
                    >
                      {drawerStatus.text}
                    </span>
                  )}
                </div>

                {!isEntire && (
                <Link
                  onClick={() =>
                    track("view_full_room", {
                      property_slug: property.slug,
                      item_id: String(drawerRoom.id),
                    })
                  }
                  to={`/r/${drawerRoom.public_token}${datesQuery}`}
                  className="hidden sm:inline-block mt-3 text-sm text-orange-500 hover:text-orange-600"
                >
                  Full room details &rarr;
                </Link>
                )}
              </div>

              <div className="min-w-0">
                {isEntire ? (
                  <div className="rounded-2xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 p-5">
                    <p className="font-semibold">Included when you book the whole place</p>
                    <p className="text-sm text-neutral-500 mt-1">
                      This room can't be booked on its own.
                    </p>
                    {entirePrice > 0 && (
                      <p className="mt-3">
                        <span className="text-xl font-semibold">{money(entirePrice)}</span>
                        <span className="text-sm text-neutral-500"> / month, whole place</span>
                      </p>
                    )}
                    <ButtonPrimary
                      className="w-full mt-4"
                      onClick={() => {
                        setDrawerToken(null);
                        scrollToBookingCard();
                      }}
                    >
                      Book the whole place
                    </ButtonPrimary>
                  </div>
                ) : (
                <RoomBookingCard
                  key={drawerRoom.id}
                  room={drawerRoom}
                  property={property}
                  checkIn={checkIn}
                  checkOut={checkOut}
                  onDatesChange={(nextIn, nextOut) => {
                    setCheckIn(nextIn);
                    setCheckOut(nextOut);
                  }}
                />
                )}
                {!isEntire && (
                <Link
                  onClick={() =>
                    track("view_full_room", {
                      property_slug: property.slug,
                      item_id: String(drawerRoom.id),
                    })
                  }
                  to={`/r/${drawerRoom.public_token}${datesQuery}`}
                  className="sm:hidden block mt-4 text-center text-sm text-orange-500 hover:text-orange-600"
                  style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
                >
                  Full room details &rarr;
                </Link>
                )}
              </div>
            </div>
          </div>
        )}
      </Modal>

      {propertyData && (
        <ImageModal
          isOpen={imageModal}
          onClose={closeImageModal}
          propertyData={propertyData}
        />
      )}
    </div>
  );
};

export default function HousePage() {
  const { slug } = useParams();
  return (
    <DetailPagetLayout>
      <HouseContent key={slug} />
    </DetailPagetLayout>
  );
}